import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import localUtil from '../../lib/util';
import VideoStreamingPlayer from '../../routes/videostreaming/player';
import Likes from '../../components/likes';
import Comments from '../../components/comments';
import ClipboardCopy from '../gui/clipboardCopy';

const YOUTUBE_REGEX = /https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)/i;
const VIDEO_EXT_REGEX = /\.(m4v|m4a|mp4|mov)$/i;
const PAGE_LIMIT = 50;

function hasVideoContent(item) {
    if (Array.isArray(item.files)) {
        const hasUploadedVideo = item.files.some(file =>
            file.s3LinkStream || (file.s3Link && VIDEO_EXT_REGEX.test(file.s3Link))
        );
        if (hasUploadedVideo) return true;
    }
    const content = `${item.ingress || ''} ${item.body || ''}`;
    if (YOUTUBE_REGEX.test(content)) return true;
    if (item.url && YOUTUBE_REGEX.test(item.url)) return true;
    if (item.youtube) return true;
    return false;
}

function getVideoThumbnailCandidates(item) {
    const candidates = [];
    if (Array.isArray(item.files)) {
        const videoFiles = item.files.filter(file =>
            file.s3LinkStreamPreview1 && (file.s3LinkStream || (file.s3Link && VIDEO_EXT_REGEX.test(file.s3Link)))
        );
        // s3LinkStreamPreview1 is stamped optimistically at upload time; the poster JPEG
        // only exists once the transcode Lambda succeeds. videoWidth is set by the backend
        // after reading the poster, so files carrying it have a verified poster.
        const verified = videoFiles.filter(file => file.videoWidth > 0);
        const unverified = videoFiles.filter(file => !(file.videoWidth > 0));
        verified.concat(unverified).forEach(file => candidates.push(file.s3LinkStreamPreview1));
    }
    const content = `${item.ingress || ''} ${item.body || ''} ${item.url || ''} ${item.youtube || ''}`;
    const thumb = localUtil.youtubeThumb(content, 'medium');
    if (thumb) candidates.push(thumb);
    if (item.urlImageUrl) candidates.push(item.urlImageUrl);
    if (item.images && item.images.length > 0) {
        const imageLink = item.images[0].s3MediumLink || item.images[0].s3SmallLink || item.images[0].s3Link;
        if (imageLink) candidates.push(imageLink);
    }
    return candidates;
}

function getYoutubeVideoId(text) {
    if (!text) return null;
    let match = text.match(/youtube\.com\/watch\?v=([^&\s]+)/);
    if (match) return match[1];
    match = text.match(/youtu\.be\/([^?&\s]+)/);
    if (match) return match[1];
    match = text.match(/youtube\.com\/shorts\/([^?&\s]+)/);
    if (match) return match[1];
    return null;
}

function formatViewCount(count) {
    if (!count || count <= 0) return null;
    if (count >= 1000000) {
        return `${(count / 1000000).toFixed(1).replace(/\.0$/, '')}M views`;
    }
    if (count >= 1000) {
        return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}K views`;
    }
    return `${count} views`;
}

function getItemDate(item) {
    if (item._feedType === 'news' || item._feedType === 'liveUpdate') {
        return item.createdDate || item.publishDate || item.date;
    }
    return item.publishDate || item.createdDate || item.date;
}

function normalizeLiveUpdate(update) {
    return {
        ...update,
        title: update.body ? update.body.substring(0, 100) : '',
        creator: update.authorName || '',
        creatorImage: update.authorImage || null,
        _feedType: 'liveUpdate',
    };
}

function getUploadedVideoFile(item) {
    if (!Array.isArray(item.files)) return null;
    return item.files.find(file =>
        file.s3LinkStream || (file.s3Link && VIDEO_EXT_REGEX.test(file.s3Link))
    ) || null;
}

@observer
class VideoGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            videoItems: [],
            loading: true,
            loadingMore: false,
            activeItem: null,
            articleOffset: 0,
            newsOffset: 0,
            liveUpdateOffset: 0,
            hasMoreArticles: true,
            hasMoreNews: true,
            hasMoreLiveUpdates: true,
            failedThumbnails: {},
        };
    }

    getThumbnail = (item) => {
        const { failedThumbnails } = this.state;
        return getVideoThumbnailCandidates(item).find(url => !failedThumbnails[url]) || null;
    }

    onThumbnailError = (e) => {
        const src = e.target && e.target.getAttribute('src');
        if (!src || this.state.failedThumbnails[src]) return;
        this.setState(prevState => ({
            failedThumbnails: { ...prevState.failedThumbnails, [src]: true },
        }));
    }

    getArticleApiUrl = () => {
        const { userStore, raceStore } = this.props.stores;
        const { isAdmin } = userStore;
        const { isRaceAdmin, isMediaWorker } = raceStore;
        const isWorkingWithMedia = isAdmin || isRaceAdmin || isMediaWorker;
        return isWorkingWithMedia ? '/api/articles/' : '/api/articles/public/';
    }

    fetchPage = async (articleOffset, newsOffset, fetchArticles, fetchNews, liveUpdateOffset, fetchLiveUpdates) => {
        const hasMoreArticles = fetchArticles !== undefined ? fetchArticles : this.state.hasMoreArticles;
        const hasMoreNews = fetchNews !== undefined ? fetchNews : this.state.hasMoreNews;
        const hasMoreLiveUpdates = fetchLiveUpdates !== undefined ? fetchLiveUpdates : this.state.hasMoreLiveUpdates;

        let articleItems = [];
        let newsItems = [];
        let liveUpdateItems = [];
        let newHasMoreArticles = hasMoreArticles;
        let newHasMoreNews = hasMoreNews;
        let newHasMoreLiveUpdates = hasMoreLiveUpdates;

        const { articleStore } = this.props.stores;

        if (hasMoreArticles) {
            const articleResponse = await util.fetchApi(this.getArticleApiUrl(), { method: 'GET' }, {
                hostname: articleStore.currentHostname,
                category: 'feed',
                hasVideo: 1,
                limit: PAGE_LIMIT,
                offset: articleOffset,
                sort: '-sort -createdDate',
                addData: 'creators,likes,comments',
            });
            if (articleResponse?.status === 200 && articleResponse.data) {
                articleItems = articleResponse.data.map(item => ({ ...item, _feedType: 'article' }));
                if (articleResponse.data.length < PAGE_LIMIT) {
                    newHasMoreArticles = false;
                }
            } else {
                newHasMoreArticles = false;
            }
        }

        if (hasMoreNews) {
            const newsResponse = await util.fetchApi('/api/news/public/', { method: 'GET' }, {
                hasRace: 1,
                hasVideo: 1,
                limit: PAGE_LIMIT,
                offset: newsOffset,
                sort: '-createdDate',
                addData: 'races,creators,likes,comments',
            });
            if (newsResponse?.status === 200 && newsResponse.data) {
                newsItems = newsResponse.data.map(item => ({ ...item, _feedType: 'news' }));
                if (newsResponse.data.length < PAGE_LIMIT) {
                    newHasMoreNews = false;
                }
            } else {
                newHasMoreNews = false;
            }
        }

        if (hasMoreLiveUpdates) {
            const liveUpdateResponse = await util.fetchApi('/api/raceliveupdates/public/', { method: 'GET' }, {
                hasVideo: 1,
                limit: PAGE_LIMIT,
                offset: liveUpdateOffset || 0,
            });
            if (liveUpdateResponse?.status === 200 && liveUpdateResponse.data) {
                liveUpdateItems = liveUpdateResponse.data.map(item => normalizeLiveUpdate(item));
                if (liveUpdateResponse.data.length < PAGE_LIMIT) {
                    newHasMoreLiveUpdates = false;
                }
            } else {
                newHasMoreLiveUpdates = false;
            }
        }

        const newItems = [...articleItems, ...newsItems, ...liveUpdateItems];
        newItems.sort((a, b) => {
            const dateA = new Date(getItemDate(a));
            const dateB = new Date(getItemDate(b));
            return dateB - dateA;
        });

        return {
            items: newItems,
            hasMoreArticles: newHasMoreArticles,
            hasMoreNews: newHasMoreNews,
            hasMoreLiveUpdates: newHasMoreLiveUpdates,
            newArticleOffset: articleOffset + articleItems.length,
            newNewsOffset: newsOffset + newsItems.length,
            newLiveUpdateOffset: (liveUpdateOffset || 0) + liveUpdateItems.length,
        };
    }

    loadAll = async () => {
        const { videoItems } = this.state;
        const isInitialLoad = videoItems.length === 0;
        this.setState({ loading: isInitialLoad, hasMoreArticles: true, hasMoreNews: true, hasMoreLiveUpdates: true, articleOffset: 0, newsOffset: 0, liveUpdateOffset: 0 });

        const result = await this.fetchPage(0, 0, true, true, 0, true);

        const { videoUuidv4 } = this.props;
        let autoSelectItem = null;
        if (videoUuidv4) {
            autoSelectItem = result.items.find(v => v.uuidv4 === videoUuidv4) || null;
        }

        this.setState({
            videoItems: result.items,
            loading: false,
            articleOffset: result.newArticleOffset,
            newsOffset: result.newNewsOffset,
            liveUpdateOffset: result.newLiveUpdateOffset,
            hasMoreArticles: result.hasMoreArticles,
            hasMoreNews: result.hasMoreNews,
            hasMoreLiveUpdates: result.hasMoreLiveUpdates,
            activeItem: autoSelectItem,
        });
    }

    loadMore = async () => {
        const { loadingMore, hasMoreArticles, hasMoreNews, hasMoreLiveUpdates, articleOffset, newsOffset, liveUpdateOffset, videoItems } = this.state;
        if (loadingMore || (!hasMoreArticles && !hasMoreNews && !hasMoreLiveUpdates)) return;

        this.setState({ loadingMore: true });

        const result = await this.fetchPage(articleOffset, newsOffset, undefined, undefined, liveUpdateOffset);

        this.setState({
            videoItems: [...videoItems, ...result.items],
            loadingMore: false,
            articleOffset: result.newArticleOffset,
            newsOffset: result.newNewsOffset,
            liveUpdateOffset: result.newLiveUpdateOffset,
            hasMoreArticles: result.hasMoreArticles,
            hasMoreNews: result.hasMoreNews,
            hasMoreLiveUpdates: result.hasMoreLiveUpdates,
        });
    }

    playItem = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const el = e.target.closest('[data-video-id]');
        if (!el) return;
        const { videoId } = el.dataset;
        const { videoItems } = this.state;
        const item = videoItems.find(v => String(v.id) === String(videoId));
        if (item) {
            const wasInGrid = !this.state.activeItem;
            this.setState({ activeItem: item }, () => {
                if (item.uuidv4) {
                    window.history.replaceState(null, '', `${window.location.pathname}#/feed/video/${item.uuidv4}`);
                }
                if (this.playerRef) {
                    const scrollContainer = document.getElementById('app');
                    if (scrollContainer) {
                        if (wasInGrid || window.innerWidth < 768) {
                            const elTop = this.playerRef.getBoundingClientRect().top;
                            const containerTop = scrollContainer.getBoundingClientRect().top;
                            const offset = elTop - containerTop + scrollContainer.scrollTop;
                            scrollContainer.scrollTo({ top: offset - 10, behavior: 'smooth' });
                        }
                    }
                }
            });
        }
    }

    closePlayer = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ activeItem: null });
        window.history.replaceState(null, '', `${window.location.pathname}#/feed/video`);
    }

    clearActiveItem = () => {
        this.setState({ activeItem: null });
        window.history.replaceState(null, '', `${window.location.pathname}#/feed/video`);
    }

    observeSentinel = (el) => {
        if (this.sentinelObserver) {
            this.sentinelObserver.disconnect();
        }
        if (!el) return;
        this.sentinelObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                this.loadMore();
            }
        }, { rootMargin: '200px' });
        this.sentinelObserver.observe(el);
    }

    onClickNewArticle = () => {
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('newArticle', {
            height: drawerHeightMediumLarge,
            isNew: true,
            category: 'feed',
            active: 1,
            userOnly: true,
            videoMode: true,
            callback: () => {
                this.loadAll();
            },
        }, 3);
    }

    retryVideoProcessing = async () => {
        const { articleStore } = this.props.stores;
        const { videoProcessingArticleId } = articleStore;
        if (!videoProcessingArticleId) return;

        await util.fetchApi(`/api/articles/${videoProcessingArticleId}`, { method: 'PATCH' }, {
            videoStatus: 'processing',
        });
        articleStore.pollVideoStatus(videoProcessingArticleId);
    }

    publishWithoutTranscript = async () => {
        const { articleStore } = this.props.stores;
        const { videoProcessingArticleId } = articleStore;
        if (!videoProcessingArticleId) return;

        await util.fetchApi(`/api/articles/${videoProcessingArticleId}`, { method: 'PATCH' }, {
            active: 1,
        });
        articleStore.updateKeyValue('videoProcessingStatus', null);
        articleStore.updateKeyValue('videoProcessingArticleId', null);
        this.loadAll();
    }

    componentDidMount() {
        this.loadAll();
        const { articleStore } = this.props.stores;
        articleStore.addRefreshPageFunction(() => this.loadAll(), 'videoGrid');
    }

    componentWillUnmount() {
        const { articleStore } = this.props.stores;
        articleStore.removeRefreshPageFunction('videoGrid');
        articleStore.stopVideoPolling();
        if (this.sentinelObserver) {
            this.sentinelObserver.disconnect();
        }
    }

    renderVideoProcessingBanner() {
        const { articleStore } = this.props.stores;
        const { videoProcessingStatus } = articleStore;

        if (!videoProcessingStatus) return null;

        if (videoProcessingStatus === 'processing') {
            return (
                <div class='alert alert-info d-flex align-items-center mb-3' role='alert'>
                    <div class='spinner-border spinner-border-sm me-2' role='status'>
                        <span class='visually-hidden'>Processing...</span>
                    </div>
                    <div>
                        <strong><Text id='article.video-processing'>Processing video...</Text></strong>
                        <div class='small mt-1'>
                            <Text id='article.video-processing-info'>Your video is being processed. It will appear in the feed when ready.</Text>
                        </div>
                    </div>
                </div>
            );
        }

        if (videoProcessingStatus === 'ready') {
            return (
                <div class='alert alert-success d-flex align-items-center mb-3' role='alert'>
                    <i class='fa-solid fa-circle-check me-2' />
                    <div>
                        <strong><Text id='article.video-ready'>Your video post is now live!</Text></strong>
                    </div>
                </div>
            );
        }

        if (videoProcessingStatus === 'failed') {
            return (
                <div class='alert alert-warning d-flex flex-column mb-3' role='alert'>
                    <div class='d-flex align-items-center'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        <strong><Text id='article.video-failed'>Video processing failed</Text></strong>
                    </div>
                    <div class='small mt-1 mb-2'>
                        <Text id='article.video-failed-info'>There was an issue processing your video. You can retry or publish without transcript.</Text>
                    </div>
                    <div class='d-flex gap-2'>
                        <button type='button' class='btn btn-sm btn-outline-primary' onClick={this.retryVideoProcessing}>
                            <i class='fa-solid fa-rotate-right me-1' />
                            <Text id='article.retry'>Retry</Text>
                        </button>
                        <button type='button' class='btn btn-sm btn-outline-secondary' onClick={this.publishWithoutTranscript}>
                            <i class='fa-solid fa-upload me-1' />
                            <Text id='article.publish-anyway'>Publish without transcript</Text>
                        </button>
                    </div>
                </div>
            );
        }

        return null;
    }

    renderPlayer() {
        const { activeItem } = this.state;
        if (!activeItem) return null;

        const videoFile = getUploadedVideoFile(activeItem);
        const content = `${activeItem.ingress || ''} ${activeItem.body || ''} ${activeItem.url || ''} ${activeItem.youtube || ''}`;
        const youtubeId = getYoutubeVideoId(content);
        const creator = activeItem.creator || '';
        const timeAgo = localUtil.getTimeAgo(getItemDate(activeItem));

        return (
            <div ref={ref => { this.playerRef = ref; }}>
                <div class='rounded-3 overflow-hidden bg-black'>
                    {videoFile && videoFile.s3LinkStream ? (
                        <VideoStreamingPlayer
                            key={videoFile.s3LinkStream}
                            videoStreamUrl={videoFile.s3LinkStream}
                            stores={this.props.stores}
                            poster={this.getThumbnail(activeItem)}
                            maxHeight='70vh'
                            id={activeItem.id}
                            transcript={videoFile.transcript}
                            transcriptionSegments={videoFile.transcriptionSegments}
                            transcriptionLanguage={videoFile.transcriptionLanguage}
                            transcriptEnglish={videoFile.transcriptEnglish}
                            transcriptionSegmentsEnglish={videoFile.transcriptionSegmentsEnglish}
                            transcriptSpanish={videoFile.transcriptSpanish}
                            transcriptionSegmentsSpanish={videoFile.transcriptionSegmentsSpanish}
                            transcriptFrench={videoFile.transcriptFrench}
                            transcriptionSegmentsFrench={videoFile.transcriptionSegmentsFrench}
                            transcriptFinnish={videoFile.transcriptFinnish}
                            transcriptionSegmentsFinnish={videoFile.transcriptionSegmentsFinnish}
                        />
                    ) : youtubeId ? (
                        <div class='w-100 position-relative' style='padding-bottom: 56.25%;'>
                            <iframe
                                class='position-absolute w-100 h-100'
                                style='top: 0; left: 0; border: none;'
                                src={`https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`}
                                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                allowFullScreen
                            />
                        </div>
                    ) : null}
                </div>
                <div class='d-flex flex-column mt-3'>
                    <span class='fw-bold text-body' style='font-size: 1.2rem; line-height: 1.3;'>
                        {activeItem.title || ''}
                    </span>
                    {(() => {
                        const views = formatViewCount(activeItem.totalViews);
                        return views || timeAgo ? (
                            <div class='text-body-secondary mt-1' style='font-size: 14px;'>
                                {views}{views && timeAgo ? ' \u2022 ' : ''}{timeAgo}
                            </div>
                        ) : null;
                    })()}
                    <div class='d-flex flex-row align-items-center mt-2' style='gap: 10px;'>
                        <span class='d-flex justify-content-center align-items-center rounded-circle bg-light text-dark border flex-shrink-0' style='width: 40px; height: 40px;'>
                            {activeItem.creatorImage ? (
                                <img src={activeItem.creatorImage} class='rounded-circle' style='width: 40px; height: 40px;' />
                            ) : (
                                <i class='fa-duotone fa-user' style='font-size: 1.1rem;' />
                            )}
                        </span>
                        {creator && (
                            <span class='fw-semibold text-body' style='font-size: 14px;'>
                                {creator}
                            </span>
                        )}
                    </div>
                </div>
                {activeItem._feedType !== 'liveUpdate' && (
                    <Likes
                        stores={this.props.stores}
                        type={activeItem._feedType === 'news' ? 'news' : 'article'}
                        objectId={activeItem.id}
                        uuidv4={activeItem.uuidv4}
                        likes={activeItem.likes}
                        showComment={false}
                        shareTitle={activeItem.title}
                        shareSummary={activeItem.ingress || activeItem.body}
                        shareImage={activeItem.images?.[0]?.s3SmallLink}
                    >
                        {activeItem.uuidv4 && <small class='text-body-secondary likes'>
                            <ClipboardCopy
                                label={<><i class='fa-regular fa-share' /> Share</>}
                                text={`${window.location.protocol}//${window.location.host}${window.location.pathname}#/feed/video/${activeItem.uuidv4}`}
                            />
                        </small>}
                    </Likes>
                )}
                {activeItem._feedType !== 'liveUpdate' && (
                    <Comments
                        stores={this.props.stores}
                        type={activeItem._feedType === 'news' ? 'news' : 'article'}
                        objectId={activeItem.id}
                        comments={activeItem.comments}
                    />
                )}
            </div>
        );
    }

    renderSidebarItem(item) {
        const thumbnail = this.getThumbnail(item);
        const timeAgo = localUtil.getTimeAgo(getItemDate(item));
        const creator = item.creator || '';
        const { activeItem } = this.state;
        const isActive = activeItem && activeItem.id === item.id;

        return (
            <div
                class={`d-flex flex-row rounded-2 overflow-hidden ${isActive ? 'bg-body-secondary' : ''}`}
                style='cursor: pointer; gap: 8px;'
                data-video-id={item.id}
                onClick={this.playItem}
            >
                <div class='position-relative flex-shrink-0' style='width: 168px; min-width: 168px;'>
                    <div class='w-100' style='aspect-ratio: 16/9; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);'>
                        {thumbnail ? (
                            <img
                                src={thumbnail}
                                alt={item.title}
                                class='w-100 h-100 rounded-2'
                                style='object-fit: cover;'
                                loading='lazy'
                                onError={this.onThumbnailError}
                            />
                        ) : (
                            <div class='d-flex justify-content-center align-items-center w-100 h-100 rounded-2'>
                                <i class='fa-solid fa-video text-light opacity-25' />
                            </div>
                        )}
                        {!isActive && (
                            <div class='position-absolute d-flex justify-content-center align-items-center' style='inset: 0;'>
                                <div class='d-flex justify-content-center align-items-center rounded-circle' style='width: 30px; height: 30px; background: rgba(255,0,0,0.85);'>
                                    <i class='fa-solid fa-play text-white' style='font-size: 10px; margin-left: 1px;' />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div class='d-flex flex-column py-1 pe-1 overflow-hidden'>
                    <div class='text-truncate fw-semibold text-body' style='font-size: 12px; line-height: 1.3;'>
                        {item.title || ''}
                    </div>
                    <small class='text-body-secondary text-truncate' style='font-size: 11px;'>
                        {creator}
                    </small>
                    <small class='text-body-secondary' style='font-size: 11px;'>
                        {timeAgo}
                    </small>
                </div>
            </div>
        );
    }

    renderGridItem(item) {
        const thumbnail = this.getThumbnail(item);
        const timeAgo = localUtil.getTimeAgo(getItemDate(item));
        const creator = item.creator || '';
        const views = formatViewCount(item.totalViews);

        return (
            <div
                class='video-grid-item d-flex flex-column'
                style='cursor: pointer;'
                data-video-id={item.id}
                onClick={this.playItem}
            >
                <div class='position-relative w-100 rounded-2 overflow-hidden' style='aspect-ratio: 16/9; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);'>
                    {thumbnail ? (
                        <img
                            src={thumbnail}
                            alt={item.title}
                            class='w-100 h-100'
                            style='object-fit: cover;'
                            loading='lazy'
                            onError={this.onThumbnailError}
                        />
                    ) : (
                        <div class='d-flex justify-content-center align-items-center w-100 h-100'>
                            <i class='fa-solid fa-video text-light opacity-25 fa-2x' />
                        </div>
                    )}
                </div>
                <div class='d-flex flex-row mt-2 px-1' style='gap: 10px;'>
                    <div class='flex-shrink-0'>
                        <span class='d-flex justify-content-center align-items-center rounded-circle bg-light text-dark border' style='width: 36px; height: 36px;'>
                            {item.creatorImage ? (
                                <img src={item.creatorImage} class='rounded-circle' style='width: 36px; height: 36px;' />
                            ) : (
                                <i class='fa-duotone fa-user' style='font-size: 1rem;' />
                            )}
                        </span>
                    </div>
                    <div class='d-flex flex-column overflow-hidden'>
                        <div class='fw-bold text-body' style='font-size: 14px; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;'>
                            {item.title || ''}
                        </div>
                        {creator && (
                            <div class='text-body-secondary' style='font-size: 13px; line-height: 1.4;'>
                                {creator}
                            </div>
                        )}
                        <div class='text-body-secondary' style='font-size: 13px; line-height: 1.4;'>
                            {views}{views && timeAgo ? ' \u2022 ' : ''}{timeAgo}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderLoadingMore() {
        return (
            <div class='d-flex justify-content-center align-items-center py-3 w-100'>
                <div class='spinner-border spinner-border-sm text-secondary' role='status'>
                    <span class='visually-hidden'>Loading...</span>
                </div>
            </div>
        );
    }

    render() {
        const { videoItems, loading, loadingMore, activeItem, hasMoreArticles, hasMoreNews, hasMoreLiveUpdates } = this.state;
        const hasMore = hasMoreArticles || hasMoreNews || hasMoreLiveUpdates;

        if (loading) {
            return (
                <div class='d-flex justify-content-center align-items-center py-5'>
                    <div class='spinner-border text-secondary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        if (videoItems.length === 0) {
            return (
                <div class='d-flex flex-column justify-content-center align-items-center py-5 text-body-secondary'>
                    <i class='fa-solid fa-video-slash fa-2x mb-3' />
                    <span><Text id='article.no-videos'>No videos found</Text></span>
                </div>
            );
        }

        // When a video is active: player + sidebar layout on large screens
        if (activeItem) {
            return (
                <div class='d-flex flex-column px-3 mt-2'>
                    {this.renderVideoProcessingBanner()}
                    {/* Large screens: side-by-side. Small screens: stacked. */}
                    <div class='video-watch-layout'>
                        <div class='video-watch-player'>
                            {this.renderPlayer()}
                        </div>
                        <div class='video-watch-sidebar'>
                            <div class='d-flex flex-column' style='gap: 8px;'>
                                {videoItems.map(item => this.renderSidebarItem(item))}
                                {loadingMore && this.renderLoadingMore()}
                                {hasMore && <div ref={this.observeSentinel} />}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // No active video: show grid
        return (
            <div class='d-flex flex-column px-3 mt-2'>
                {this.renderVideoProcessingBanner()}
                <div class='d-flex flex-row flex-wrap' style='gap: 12px;'>
                    {videoItems.map(item => this.renderGridItem(item))}
                    {loadingMore && this.renderLoadingMore()}
                    {hasMore && <div ref={this.observeSentinel} />}
                </div>
            </div>
        );
    }
}

export default VideoGrid;
